import { createStore, createEvent, createEffect, combine } from 'effector';
import { get } from 'lodash';
import request, { ClientOptions } from '@helpers/request';
import api from '@constants/endpoints';
import createPreloadersStore from './preloaders';
import IUser, { NicknameChange, IPlus, AccountChange } from '@interfaces/IUser';
import IPost from '../interfaces/IPost';
import IGame from '@interfaces/IGame';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from './pagination';
import createFiltersStore from './filters';
import { unblockUser, blockUser, unlinkDiscord } from './users';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import {
  block,
  unblock,
  hidePost,
  unhidePost,
  updateItemHandler,
  updateFaceItemHandler,
  updateRemovefaceItemHandler,
  facePost,
  removefacePost,
} from './posts';

export const reset = createEvent('User store reset');

const defaultFilters = {
  sort: 'createdDateTime,desc',
};

export const getUserGames = createEffect({
  name: 'getUserGames',
  async handler(params: { userId: IUser['id']; pagination?: PaginationProps }) {
    const current = params.pagination?.current ?? 1;
    const pageSize = params.pagination?.pageSize ?? 20;

    return await request(api.profile.getUserGames, {
      urlParams: { accountId: params.userId },
      data: {
        pagination: {
          page: current - 1,
          pageSize,
          type: 'PAGE',
        },
      },
    });
  },
});

export const getItem = createEffect({
  name: 'getItem',
  async handler(params: { userId: IUser['id']; clientOptions?: ClientOptions }) {
    const { userId, clientOptions } = params;
    const userResponse = await request(api.users.getItem, { urlParams: { userId } }, clientOptions);
    const user: IUser = userResponse.data.body;

    return user;
  },
});

export const getIsPlus = createEffect({
  name: 'getIsPlus',
  async handler(params: { userId: IUser['id'] }) {
    const { userId } = params;
    const response = await request(api.users.getIsPlus, { urlParams: { userId } });
    return response;
  },
});

export const addPlus = createEffect({
  name: 'addPlus',
  async handler(data: any) {
    const response = await request(api.users.addPlus, {
      data,
    });
    return response;
  },
});

export const continuePlus = createEffect({
  name: 'continuePlus',
  async handler(data: any) {
    const response = await request(api.users.continuePlus, {
      data,
    });
    return response;
  },
});

export const revokePlus = createEffect({
  name: 'revokePlus',
  async handler(data: { accountId: IUser['id'] }) {
    const response = await request(api.users.revokePlus, {
      data,
    });
    return response;
  },
});

export const getPosts = createEffect({
  name: 'getPosts',
  async handler(params: {
    userId: IUser['id'];
    pagination?: PaginationProps;
    filters?: Record<string, string>;
  }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };
    const { current, defaultPageSize = 50 } = paginationParams;

    let filtersParams = filters.getState();
    if (params.filters) {
      setFilters(params.filters);
      filtersParams = params.filters;
      if (!params.pagination) {
        paginationParams.current = 1;
      }
    }

    const response = await request(api.posts.getList, {
      params: {
        authorId: params.userId,
        paged: true,
        size: defaultPageSize,
        page: (current || 1) - 1,
        ...defaultFilters,
        ...removeUndefinedFilters(filtersParams),
      },
    });
    return response;
  },
});

export const getPlusHistory = createEffect({
  name: 'getPlusHistory',
  async handler(params: { paginationPlusHistory?: PaginationProps; userId: IUser['id'] }) {
    const paginationParams = {
      ...params?.paginationPlusHistory,
    };

    const requestParams = {
      size: paginationParams?.pageSize ?? 10,
      page: (paginationParams?.current || 1) - 1,
    };

    const response = await request(api.users.getPlusHistory, {
      urlParams: { accountId: params.userId },
      params: requestParams,
    });

    return response;
  },
});

export const getNicknameHistory = createEffect({
  name: 'getNicknameHistory',
  async handler(params: { userId: IUser['id'] }) {
    const response = await request(api.users.getNicknameHistory, {
      urlParams: { userId: params.userId },
      data: {
        pagination: {
          page: 0,
          pageSize: 100,
          type: 'PAGE',
        },
      },
    });

    return response;
  },
});

export const getAccountChangeHistory = createEffect({
  name: 'getAccountChangeHistory',
  async handler({
    userId,
    page = 0,
    pageSize = 20,
  }: {
    userId: IUser['id'];
    page?: number;
    pageSize?: number;
  }): Promise<AccountChange[]> {
    const response = await request(api.users.getAccountChangeHistory, {
      urlParams: { userId },
      data: { pagination: { page, pageSize, type: 'PAGE' } },
    });

    return response.data.body?.changes ?? [];
  },
});

export const getModerationHistory = createEffect({
  name: 'getModerationHistory',
  async handler(params: { paginationModerationHistory?: PaginationProps; userId: IUser['id'] }) {
    const paginationParams = {
      ...params?.paginationModerationHistory,
    };

    const requestParams = {
      size: paginationParams?.pageSize ?? 10,
      page: (paginationParams?.current || 1) - 1,
    };

    const response = await request(api.users.getModerationHistory, {
      urlParams: { accountId: params.userId },
      params: requestParams,
    });

    return response;
  },
});

const { pagination } = createPaginationStore({
  getList: getPosts,
  listName: 'userPosts',
  resetEvent: reset,
});

export const setPaginationUserGames = createEvent<Pick<PaginationProps, 'current' | 'pageSize'>>();

const paginationUserGames = createStore<PaginationProps & { total: number }>({
  current: 1,
  pageSize: 20,
  total: 0,
})
  .on(setPaginationUserGames, (state, payload) => ({
    ...state,
    ...payload,
    current: payload.current ?? state.current,
    pageSize: payload.pageSize ?? state.pageSize,
  }))
  .on(getItem.done, (state, { result }) => ({
    ...state,
    total: (result as any)?.gamesCount ?? 0,
  }))
  .reset(reset);

export { paginationUserGames };

const { pagination: paginationModerationHistory, setPagination: setPaginationModerationHistory } =
  createPaginationStore({
    getList: getModerationHistory,
    listName: 'moderationHistory',
    resetEvent: reset,
    bodyPath: ['data', 'body'],
  });

export { paginationModerationHistory, setPaginationModerationHistory };

const { pagination: paginationPlusHistory, setPagination: setPaginationPlusHistory } =
  createPaginationStore({
    getList: getPlusHistory,
    listName: 'plusHistory',
    resetEvent: reset,
    bodyPath: ['data', 'body', 'records'],
  });

export { setPaginationPlusHistory };

const { filters, setFilters } = createFiltersStore({
  listName: 'userPosts',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { filters, setFilters };

const preloaders = createPreloadersStore([
  getItem,
  getPosts,
  getIsPlus,
  unblockUser,
  block,
  hidePost,
  unhidePost,
  facePost,
  removefacePost,
  getNicknameHistory,
  getPlusHistory,
  getUserGames,
  getModerationHistory,
]);

const item = createStore<IUser | null>(null)
  .on(getItem.done, (state, { result }) => result)
  .on(unlinkDiscord.done, (state, { params }) => {
    if (!state || String(state.id) !== String(params.userId)) return state;
    return {
      ...state,
      linkedServices: state.linkedServices?.filter((service) => service.provider !== 'DISCORD'),
    };
  })
  .on(unblockUser.done, (state, { result }) => ({
    ...state,
    ...get(result, ['data', 'body'], {}),
  }))
  .on(blockUser.done, (state, { result }) => ({
    ...state,
    ...get(result, ['data', 'body'], {}),
  }))
  .reset(reset);

const posts = createStore<IPost[]>([])
  .on(getPosts.done, (state, { result }) => [...result.data.content])
  .on(block.done, updateItemHandler)
  .on(unblock.done, updateItemHandler)
  .on(hidePost.done, updateItemHandler)
  .on(unhidePost.done, updateItemHandler)
  .on(facePost.done, updateFaceItemHandler)
  .on(removefacePost.done, updateRemovefaceItemHandler)
  .reset(reset);

const plus = createStore<IPlus | null>(null)
  .on(getIsPlus.done, (state, { result }) =>
    get(result, ['data', 'body', 'activeSubscription'], {}),
  )
  .on(addPlus.done, (state, { result }) => ({
    ...state,
    ...get(result, ['data', 'body'], {}),
  }))
  .on(continuePlus.done, (state, { result }) => ({
    ...state,
    ...get(result, ['data', 'body'], {}),
  }))
  .on(revokePlus.done, (state, { result }) => ({
    ...state,
    ...get(result, ['data', 'body'], {}),
  }))
  .reset(reset);

const userGames = createStore<IGame[]>([])
  .on(getUserGames.done, (state, { result }) => get(result, ['data', 'body', 'page'], []))
  .reset(reset);

export const nicknameHistory = createStore<NicknameChange[]>([])
  .on(getNicknameHistory.done, (state, { result }) => get(result, ['data', 'body', 'changes'], []))
  .reset(reset);

export const moderationHistoryRaw = createStore<any>(null)
  .on(getModerationHistory.done, (state, { result }) => get(result, ['data', 'body'], null))
  .reset(reset);

export const plusHistoryRaw = createStore<any>(null)
  .on(getPlusHistory.done, (state, { result }) => get(result, ['data', 'body', 'records'], null))
  .reset(reset);

const user = combine({
  preloaders,
  item,
  posts,
  pagination,
  filters,
  plus,
  nicknameHistory,
  plusHistoryRaw,
  paginationPlusHistory,
  setPaginationPlusHistory,
  moderationHistoryRaw,
  paginationModerationHistory,
  setPaginationModerationHistory,
  userGames,
  paginationUserGames,
  setPaginationUserGames,
});

export default user;
